import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { saveAiUserData } from '../services/aiUserDataService';
import '../styles/Profile.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboard, faStar, faCheckCircle, faCheck } from '@fortawesome/free-solid-svg-icons';

const Profile: React.FC = () => {
  const { user, signOut, reloadUser } = useAuth();
  const navigate = useNavigate();
  
  // Dados pessoais
  const [age, setAge] = useState('');
  const [occupation, setOccupation] = useState('');
  const [monthlyIncome, setMonthlyIncome] = useState('');
  const [riskProfile, setRiskProfile] = useState<'conservative' | 'moderate' | 'aggressive'>('moderate');
  const [investmentGoals, setInvestmentGoals] = useState<string[]>([]);
  
  // Dados de imposto
  const [entityType, setEntityType] = useState<'individual' | 'company'>('individual');
  const [profession, setProfession] = useState('');

  // Sistema de indicação
  const [referralCode, setReferralCode] = useState('');
  const [referralCount, setReferralCount] = useState(0);
  const [premiumReferralCount, setPremiumReferralCount] = useState(0);
  const [freeMonthsEarned, setFreeMonthsEarned] = useState(0);
  const [freeMonthsUsed, setFreeMonthsUsed] = useState(0);
  const [copied, setCopied] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [daysLeft, setDaysLeft] = useState<number | null>(null);

  // Cálculos para meses grátis e bônus premium
  const freeMonthsAvailable = freeMonthsEarned - freeMonthsUsed;
  const nextFreeMonthProgress = referralCount % 5;
  const nextPremiumBonus = premiumReferralCount % 2;

  // Função para copiar o link de indicação
  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  // Função para alternar meta de investimento
  const toggleGoal = (goal: string) => {
    setInvestmentGoals(prev =>
      prev.includes(goal)
        ? prev.filter(g => g !== goal)
        : [...prev, goal]
    );
  };

  const loadUserData = useCallback(async () => {
    if (!user) return;
    
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      
      if (userData) {
        // Cálculo de dias restantes do plano free
        if (!userData.isPremium && userData.createdAt) {
          const createdDate = typeof userData.createdAt === 'string'
            ? new Date(userData.createdAt)
            : userData.createdAt.toDate ? userData.createdAt.toDate() : new Date(userData.createdAt);
          const now = new Date();
          const diffMs = now.getTime() - createdDate.getTime();
          const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
          const freeDays = 30;
          const left = freeDays - diffDays;
          setDaysLeft(left);
          if (left <= 0) setShowPaymentModal(true);
        }
        setAge(userData.age?.toString() || '');
        setOccupation(userData.occupation || '');
        setMonthlyIncome(userData.monthlyIncome?.toString() || '');
        setRiskProfile(userData.riskProfile || 'moderate');
        setInvestmentGoals(userData.investmentGoals || []);
        setEntityType(userData.entityType || 'individual');
        setProfession(userData.profession || '');
        
        const code = userData.referralCode || user.uid.substring(0, 8).toUpperCase();
        setReferralCode(code);
        
        if (!userData.referralCode) {
          await updateDoc(doc(db, 'users', user.uid), { referralCode: code });
        }
        
        setReferralCount(userData.referralCount || 0);
        setPremiumReferralCount(userData.premiumReferralCount || 0);
        setFreeMonthsEarned(userData.freeMonthsEarned || 0);
        setFreeMonthsUsed(userData.freeMonthsUsed || 0);
      } else {
        const code = user.uid.substring(0, 8).toUpperCase();
        setReferralCode(code);
        
        await updateDoc(doc(db, 'users', user.uid), {
          referralCode: code,
          referralCount: 0,
          premiumReferralCount: 0,
          freeMonthsEarned: 0,
          freeMonthsUsed: 0,
        });
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
  }, [user]);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  const handleSave = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const userData: any = {};
      if (age && !isNaN(Number(age)) && Number(age) > 0) userData.age = Number(age);
      if (occupation) {
        userData.occupation = occupation;
      } else if (profession) {
        userData.occupation = profession;
      }
      if (monthlyIncome && !isNaN(Number(monthlyIncome)) && Number(monthlyIncome) > 0) userData.monthlyIncome = Number(monthlyIncome);
      if (riskProfile) userData.riskProfile = riskProfile;
      if (investmentGoals && investmentGoals.length > 0) userData.investmentGoals = investmentGoals;
      if (entityType) userData.entityType = entityType;
      if (profession) userData.profession = profession;

      await Promise.all([
        updateDoc(doc(db, 'users', user.uid), userData),
        saveAiUserData(user.uid, userData)
      ]);

      // Atualiza cache local do usuário para refletir imediatamente no AuthContext
      try {
        const cacheKey = `myMoney_userCache_${user.uid}`;
        const cache = {
          data: {
            ...user,
            ...userData,
            age: userData.age,
            monthlyIncome: userData.monthlyIncome
          },
          timestamp: Date.now()
        };
        localStorage.setItem(cacheKey, JSON.stringify(cache));
      } catch {}
    } catch (error) {
      setLoading(false);
      setShowError(true);
      console.error('Erro ao salvar dados:', error);
    }

      await reloadUser();
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        window.location.href = '/reports';
      }, 1200);
    // ...existing code...
  };

  // Limpa o snackbar de erro automaticamente
  React.useEffect(() => {
    if (showError) {
      const t = setTimeout(() => setShowError(false), 4200);
      return () => clearTimeout(t);
    }
  }, [showError]);

  return (
    <div className="profile-page">
      {/* Modal de pagamento se plano free vencido */}
      {showPaymentModal && (
        <div className="modal-overlay">
          <div className="modal-payment">
            <h2>Seu período gratuito expirou</h2>
            <p>Para continuar usando o MyMoney, faça o pagamento do plano Premium.</p>
            <button className="pay-btn" onClick={() => navigate('/premium')}>Ir para pagamento</button>
          </div>
        </div>
      )}
      <header className="page-header">
        <button onClick={() => navigate('/dashboard')} className="back-btn">← Voltar</button>
        <h1>Meu Perfil</h1>
        {daysLeft !== null && (
          <div style={{ marginLeft: 12, color: '#666', fontWeight: 600 }}>{daysLeft > 0 ? `${daysLeft} dias restantes do trial` : 'Trial expirado'}</div>
        )}
        <button onClick={signOut} className="logout-btn">Sair</button>
      </header>

      {showSuccess && (
        <div className="modal-success-overlay">
          <div className="modal-success">
            <h2><FontAwesomeIcon icon={faCheckCircle} /> Dados salvos com sucesso!</h2>
          </div>
        </div>
      )}

      {showError && (
        <div style={{ margin: '10px 0' }}>
          <div className="error-banner" style={{ background: '#fee', border: '1px solid #f88', borderRadius: 8, padding: 12, color: '#c33' }}>
            Erro ao salvar os dados. Tente novamente.
          </div>
        </div>
      )}

      <div className="profile-card">
        <div className="profile-header">
          <img 
            src={user?.photoURL || 'https://via.placeholder.com/100'} 
            alt="Foto de perfil" 
            className="profile-photo"
          />
          <div>
            <h2>{user?.displayName}</h2>
            <p className="profile-email">{user?.email}</p>
            {user?.isPremium ? (
              <span className="premium-badge"><FontAwesomeIcon icon={faStar} /> Premium</span>
            ) : (
              <span className="free-badge">🆓 Plano Grátis</span>
            )}
            {/* Botão de suporte discreto logo abaixo da identificação do plano */}
            <div style={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '16px 0 0 0' }}>
              <a
                href="https://wa.me/5524998311290"
                target="_blank"
                rel="noopener noreferrer"
                className="support-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  background: '#f3f4f6',
                  color: '#25D366',
                  padding: '8px 18px',
                  borderRadius: 8,
                  textAlign: 'center',
                  textDecoration: 'none',
                  fontWeight: 500,
                  fontSize: 15,
                  boxShadow: 'none',
                  border: '1px solid #e5e7eb',
                  transition: 'background 0.2s',
                }}
                onMouseOver={e => (e.currentTarget.style.background = '#e5e7eb')}
                onMouseOut={e => (e.currentTarget.style.background = '#f3f4f6')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 32 32" fill="none"><circle cx="16" cy="16" r="16" fill="#25D366"/><path d="M22.5 18.5c-.3-.2-1.8-.9-2.1-1-0.3-.1-.5-.2-.7.2s-0.8 1-1 1.2c-0.2.2-.4.2-.7.1-0.3-.2-1.2-.4-2.3-1.3-0.8-.7-1.4-1.5-1.6-1.8-0.2-.3 0-.5.1-.7.1-.1.2-.3.3-.4.1-.1.1-.2.2-.3s0-.2 0-.3c0-.1-.7-1.7-.9-2.3-0.2-.6-.4-.5-.6-.5h-0.5c-0.2 0-.5.1-.7.3-0.2.2-1.1 1.1-1.1 2.7s1.1 3.1 1.2 3.3c0.1.2 2.2 3.4 5.4 4.6 0.8.3 1.4.5 1.9.6 0.8.1 1.5.1 2.1.1 0.6 0 1.8-.7 2-1.4 0.2-.7 0.2-1.3 0.1-1.4-0.1-.1-0.3-.2-0.6-.3z" fill="#fff"/></svg>
                Suporte via WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>

      {user?.isPremium && (
        <div className="premium-support-section">
          <h2><FontAwesomeIcon icon={faStar} /> Suporte Prioritário Premium</h2>
          <div className="support-content">
            <div className="support-feature">
              <span className="support-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2 7h7l-5.5 4 2 7L12 16l-7.5 4 2-7L1 9h7z"/></svg></span>
              <div>
                <h4>Atendimento Prioritário</h4>
                <p>Resposta em até 2 horas em dias úteis</p>
              </div>
            </div>
            <div className="support-feature">
              <span className="support-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17 2.3a1 1 0 0 0-1.2-.9l-3.7 1a1 1 0 0 0-.7.9v5.5l1 1V5.4l3-0.8v9.3l-3 0.8v1.9l4-1.1A2 2 0 0 0 19 21v1h2v-3.3c0-1.2-.5-2.4-1.3-3.2l-0.7-0.7v-9.3z"/></svg></span>
              <div>
                <h4>WhatsApp Direto</h4>
                <p>Atendimento via WhatsApp Business</p>
              </div>
            </div>
            
          </div>
          <button
            className="contact-support-btn"
            onClick={() => window.open('https://wa.me/5524998311290?text=Atendimento%20Premium', '_blank')}
          >
            Contatar Suporte Prioritário
          </button>
        </div>
      )}

      <div className="referral-section">
        <h2>Sistema de Indicação</h2>
        <p className="referral-description">
          Convide amigos e ganhe meses grátis de Premium!
        </p>

        <div className="referral-stats">
          <div className="stat">
            <div className="stat-number">{referralCount}</div>
            <div className="stat-label">Indicações</div>
          </div>
          <div className="stat">
            <div className="stat-number">{premiumReferralCount}</div>
            <div className="stat-label">Viraram Premium</div>
          </div>
          <div className="stat highlight">
            <div className="stat-number">{freeMonthsAvailable}</div>
            <div className="stat-label">Meses Grátis</div>
          </div>
        </div>

        <div className="referral-rules">
          <h3>Como funciona:</h3>
          <ul>
            <li><FontAwesomeIcon icon={faCheck} /> A cada <strong>5 indicações</strong> → +1 mês Premium grátis</li>
            <li><FontAwesomeIcon icon={faStar} /> A cada <strong>2 que virarem Premium</strong> → +1 mês Premium grátis</li>
            <li>🎉 Meses grátis podem ser acumulados!</li>
          </ul>
        </div>

        <div className="progress-bars">
          <div className="progress-item">
            <div className="progress-header">
              <span>Próximo mês grátis (indicações)</span>
              <span>{nextFreeMonthProgress}/5</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${(nextFreeMonthProgress / 5) * 100}%` }}
              />
            </div>
          </div>

          <div className="progress-item">
            <div className="progress-header">
              <span>Bônus Premium</span>
              <span>{nextPremiumBonus}/2</span>
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill premium" 
                style={{ width: `${(nextPremiumBonus / 2) * 100}%` }}
              />
            </div>
          </div>
        </div>

        <div className="referral-code-section">
          <label>Seu código de indicação:</label>
          <div className="code-input-group">
            <input 
              type="text" 
              value={referralCode} 
              readOnly 
              className="referral-code-input"
            />
            <button onClick={copyReferralLink} className="copy-btn">
              {copied ? '✓ Copiado!' : <><FontAwesomeIcon icon={faClipboard} /> Copiar Link</>}
            </button>
          </div>
          <small>Compartilhe este link com seus amigos!</small>
        </div>
      </div>

      <div className="profile-section">
        <h2> Dados para IA e Impostos</h2>
        <p className="section-description">
          Ajude a IA a dar melhores sugestões e calcule seus impostos
        </p>

        <div className="form-grid">
          <div className="form-group">
            <label>Tipo de Entidade</label>
            <select 
              value={entityType}
              onChange={(e) => setEntityType(e.target.value as any)}
            >
              <option value="individual">Pessoa Física</option>
              <option value="company">Pessoa Jurídica</option>
            </select>
          </div>

          <div className="form-group">
            <label>Profissão</label>
            <input 
              type="text" 
              placeholder="Ex: Desenvolvedor"
              value={profession}
              onChange={(e) => setProfession(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Idade</label>
            <input 
              type="number" 
              placeholder="Ex: 28"
              value={age}
              onChange={(e) => setAge(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Renda Mensal (R$)</label>
            <input 
              type="number" 
              placeholder="Ex: 5000"
              value={monthlyIncome}
              onChange={(e) => setMonthlyIncome(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Perfil de Risco</label>
            <select 
              value={riskProfile}
              onChange={(e) => setRiskProfile(e.target.value as any)}
            >
              <option value="conservative">Conservador</option>
              <option value="moderate">Moderado</option>
              <option value="aggressive">Agressivo</option>
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Objetivos de Investimento</label>
          <div className="goals-grid">
            {[
              'Aposentadoria',
              'Comprar Casa',
              'Viagem',
              'Emergências',
              'Educação',
              'Renda Extra'
            ].map(goal => (
              <button
                key={goal}
                className={`goal-btn ${investmentGoals.includes(goal) ? 'active' : ''}`}
                onClick={() => toggleGoal(goal)}
              >
                {investmentGoals.includes(goal) ? '✓ ' : ''}{goal}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="profile-actions">
        <button 
          onClick={handleSave} 
          disabled={loading}
          className="save-btn"
        >
          {loading ? 'Salvando...' : '💾 Salvar Alterações'}
        </button>
      </div>

    </div>
  );
};

export default Profile;
